/**
 * CardioSection — tabla de sesiones de cardio (día/tipo/duración/intensidad/notas).
 *
 * Mejoras respecto a la versión anterior:
 *  - Botones ↑↓ para reordenar sesiones (igual que en rutina/dieta).
 *  - "Tipo" ya no es un campo libre: se elige del catálogo global filtrado a
 *    `Cardio` vía `ExercisePickerModal` (con su GIF y su descripción). El coach
 *    puede igualmente sobreescribir el nombre si quiere algo personalizado.
 *  - Cada sesión muestra su mini-GIF si lo eligió del catálogo.
 */
import { useEffect, useState } from "react";
import { ensureIds, genId, type CardioData, type CardioSession } from "../../../types/plan";
import { Icon } from "../../../lib/Icon";
import { InlineEditable } from "../InlineEditable";
import { PageHeader, SectionPage } from "./section-shared";
import { ExercisePickerModal } from "../ExercisePickerModal";
import { absUploadUrl } from "../../../api/client";
import { exerciseGifUrl, type Exercise } from "../../../api/exercises";

interface CardioSectionProps {
  data: CardioData;
  onChange: (next: CardioData) => void;
  readonly?: boolean;
}

export function CardioSection({ data, onChange, readonly }: CardioSectionProps) {
  // Garantizar que cada session tenga id estable (necesario para fieldPath
  // persistente al reordenar). Si faltan ids, hacemos un solo write.
  useEffect(() => {
    if (readonly) return;
    const withIds = ensureIds(data.sessions || [], "cs");
    if (withIds !== data.sessions) {
      onChange({ ...data, sessions: withIds });
    }
  }, [data, readonly, onChange]);

  /** Si se está abriendo el picker para una sesión específica, este es su
   *  índice; null si el picker está cerrado. */
  const [pickerForIndex, setPickerForIndex] = useState<number | null>(null);

  const updateSession = (i: number, patch: Partial<CardioSession>) => {
    const sessions = [...(data.sessions || [])];
    sessions[i] = { ...sessions[i], ...patch };
    onChange({ ...data, sessions });
  };

  const removeSession = (i: number) => {
    const sessions = [...(data.sessions || [])];
    sessions.splice(i, 1);
    onChange({ ...data, sessions });
  };

  const moveSession = (i: number, dir: -1 | 1) => {
    const target = i + dir;
    const sessions = [...(data.sessions || [])];
    if (target < 0 || target >= sessions.length) return;
    [sessions[i], sessions[target]] = [sessions[target], sessions[i]];
    onChange({ ...data, sessions });
  };

  const addSession = () => {
    const sessions = [
      ...(data.sessions || []),
      { id: genId("cs"), day: "Lunes", type: "Cinta", duration: "20 min", intensity: "Zona 2", notes: "" },
    ];
    onChange({ ...data, sessions });
  };

  /** Cuando el coach elige un ejercicio cardiovascular del picker, lo
   *  fusionamos con la sesión: nombre/libId/GIF. Si la sesión no traía
   *  duración/intensidad sugerimos defaults razonables. */
  const handlePick = (ex: Exercise) => {
    if (pickerForIndex == null) return;
    const cur = data.sessions[pickerForIndex];
    updateSession(pickerForIndex, {
      type: ex.name,
      libId: ex.id,
      gifUrl: exerciseGifUrl(ex) || null,
      // Solo sugerir si la sesión venía vacía — no pisamos cambios del coach.
      duration: cur?.duration?.trim() ? cur.duration : "20 min",
      intensity: cur?.intensity?.trim() ? cur.intensity : "Zona 2",
    });
    setPickerForIndex(null);
  };

  return (
    <SectionPage readonly={readonly}>
      <PageHeader eyebrow="Cardio" title="Cardio" />
      <p className="al-pg-summary">
        <InlineEditable
          value={data.summary || ""}
          onChange={(v) => onChange({ ...data, summary: v })}
          multiline
          readonly={readonly}
          placeholder="Resumen / objetivo del cardio…"
          fieldPath="summary"
        />
      </p>
      <table className="al-pg-table al-pg-table-full">
        <thead>
          <tr>
            <th>Día</th>
            <th>Tipo</th>
            <th>Duración</th>
            <th>Intensidad</th>
            <th>Notas</th>
            {!readonly && <th aria-label="Acciones"></th>}
          </tr>
        </thead>
        <tbody>
          {data.sessions.map((s, i) => {
            const sid = s.id || `idx${i}`;
            const isLast = i === data.sessions.length - 1;
            return (
              <tr key={s.id || i}>
                <td>
                  <b>
                    <InlineEditable value={s.day} onChange={(v) => updateSession(i, { day: v })} readonly={readonly} fieldPath={`sess.${sid}.day`} />
                  </b>
                </td>
                <td>
                  {/* Tipo = ejercicio del catálogo. Muestra mini-GIF si lo hay
                      y un botón "elegir" que abre el picker filtrado a Cardio.
                      EN VISTA PÚBLICA (readonly + libId): el GIF y el nombre
                      son clickeables → abren /exercise/{libId} en pestaña
                      nueva igual que rutina. Para sesiones sin libId (texto
                      manual del coach) se muestra como antes, sin link. */}
                  <div className="al-cardio-type-cell">
                    {readonly && s.libId ? (
                      <a
                        className="al-cardio-type-link"
                        href={`/exercise/${s.libId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Ver ${s.type}`}
                        aria-label={`Abrir detalle de ${s.type}`}
                      >
                        {s.gifUrl && (
                          <span className="al-cardio-type-thumb">
                            <img src={absUploadUrl(s.gifUrl)} alt="" decoding="async" />
                          </span>
                        )}
                        <span className="al-cardio-type-name">{s.type}</span>
                      </a>
                    ) : (
                      <>
                        {s.gifUrl && (
                          <span className="al-cardio-type-thumb" title="GIF del ejercicio">
                            <img src={absUploadUrl(s.gifUrl)} alt="" decoding="async" />
                          </span>
                        )}
                        <span className="al-cardio-type-name">
                          <InlineEditable
                            value={s.type}
                            onChange={(v) => updateSession(i, { type: v })}
                            readonly={readonly}
                            fieldPath={`sess.${sid}.type`}
                          />
                        </span>
                        {!readonly && (
                          <button
                            type="button"
                            className="al-cardio-type-pick"
                            onClick={() => setPickerForIndex(i)}
                            title="Elegir ejercicio de cardio del catálogo"
                          >
                            <Icon name="search" size={11} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
                <td>
                  <InlineEditable value={s.duration} onChange={(v) => updateSession(i, { duration: v })} readonly={readonly} fieldPath={`sess.${sid}.duration`} />
                </td>
                <td>
                  <InlineEditable value={s.intensity} onChange={(v) => updateSession(i, { intensity: v })} readonly={readonly} fieldPath={`sess.${sid}.intensity`} />
                </td>
                <td>
                  <InlineEditable value={s.notes} onChange={(v) => updateSession(i, { notes: v })} readonly={readonly} fieldPath={`sess.${sid}.notes`} />
                </td>
                {!readonly && (
                  <td>
                    <div className="al-cardio-row-actions">
                      {/* Mismas flechas que RoutineSection — texto plano para
                          que coincidan visualmente (el componente Icon no
                          acepta `style` así que tampoco rotamos un arrow). */}
                      <button
                        type="button"
                        className="al-ex-move-btn"
                        onClick={() => moveSession(i, -1)}
                        disabled={i === 0}
                        title="Subir"
                        aria-label="Subir"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="al-ex-move-btn"
                        onClick={() => moveSession(i, 1)}
                        disabled={isLast}
                        title="Bajar"
                        aria-label="Bajar"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="al-ex-del-btn"
                        onClick={() => removeSession(i)}
                        title="Eliminar"
                      >
                        <Icon name="x" size={12} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!readonly && (
        <button type="button" className="al-day-add-ex" onClick={addSession} style={{ marginTop: 8 }}>
          <Icon name="plus" size={12} /> Agregar sesión
        </button>
      )}

      {/* Picker fijado a Cardio: solo muestra los ejercicios cardiovasculares
          del catálogo (cinta, escalador, elíptica, remo, salto de cuerda…)
          con su GIF. */}
      <ExercisePickerModal
        open={pickerForIndex != null}
        onClose={() => setPickerForIndex(null)}
        onPick={handlePick}
        lockGroup="Cardio"
      />
    </SectionPage>
  );
}
